import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    MenuItem, Box,
} from '@mui/material';
import { ITask, ITaskRequest, TaskPriority } from '../../interfaces/task.interface';
import { TaskService } from '../../services/task.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { EmployeeAutocomplete } from '../common/EmployeeAutocomplete';
import ProjectSelect, { projectTagLabel, useProjectOptions } from '../project/ProjectSelect'; // P1-A project

interface Props {
    open: boolean;
    task?: ITask | null;
    onClose: () => void;
    onSaved: () => void;
}

interface UserOption {
    id: string;
    username: string;
}

const toLocalInput = (iso?: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const TaskForm: React.FC<Props> = ({ open, task, onClose, onSaved }) => {
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [assignee, setAssignee] = useState<UserOption | null>(null);
    const [dueDate, setDueDate] = useState('');
    const [priority, setPriority] = useState<TaskPriority>('MEDIUM');
    const [saving, setSaving] = useState(false);
    const [projectId, setProjectId] = useState(''); // P1-A project
    const projectOptions = useProjectOptions(institutionId); // P1-A project

    useEffect(() => {
        if (!open) return;
        if (task) {
            setTitle(task.title);
            setDescription(task.description || '');
            setAssignee({ id: task.assigneeUserId, username: task.assigneeName || '' });
            setDueDate(toLocalInput(task.dueDate));
            setPriority(task.priority);
            setProjectId(task.projectId ?? '');
        } else {
            setTitle('');
            setDescription('');
            setAssignee(null);
            setDueDate('');
            setPriority('MEDIUM');
            setProjectId('');
        }
    }, [task, open]);

    const handleSubmit = async () => {
        if (!title.trim()) {
            toast.showError('Title is required');
            return;
        }
        if (!assignee) {
            toast.showError('Assignee is required');
            return;
        }
        const request: ITaskRequest = {
            title: title.trim(),
            description: description || null,
            assigneeUserId: assignee.id,
            dueDate: dueDate ? new Date(dueDate).toISOString() : null,
            priority,
            projectId: projectId || null, // P1-A project
        };
        setSaving(true);
        try {
            if (task) {
                await TaskService.update(task.id, institutionId, request);
            } else {
                await TaskService.create(institutionId, request);
            }
            onSaved();
            onClose();
        } catch (err) {
            toast.showApiError(err, 'Failed to save task');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-help-page="tasks.form.page">
            <DialogTitle>{task ? 'Edit Task' : 'New Task'}</DialogTitle>
            <DialogContent>
                <Box data-help-section="tasks.form.section.details" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField data-help="tasks.form.field.title" label="Title" value={title} onChange={e => setTitle(e.target.value)} fullWidth required inputProps={{ maxLength: 200 }} />
                    <TextField data-help="tasks.form.field.description" label="Description" value={description} onChange={e => setDescription(e.target.value)} fullWidth multiline minRows={3} inputProps={{ maxLength: 10000 }} />
                    <EmployeeAutocomplete<UserOption>
                        data-help="tasks.form.field.assignee"
                        label="Assignee"
                        placeholder="Search users..."
                        size="medium"
                        fullWidth
                        required
                        value={assignee}
                        onChange={setAssignee}
                        onError={(err) => toast.showApiError(err, 'Failed to search users')}
                    />
                    <TextField data-help="tasks.form.field.dueDate" label="Due Date (optional)" type="datetime-local" value={dueDate} onChange={e => setDueDate(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
                    <TextField data-help="tasks.form.field.priority" select label="Priority" value={priority} onChange={e => setPriority(e.target.value as TaskPriority)} fullWidth>
                        <MenuItem value="LOW">Low</MenuItem>
                        <MenuItem value="MEDIUM">Medium</MenuItem>
                        <MenuItem value="HIGH">High</MenuItem>
                    </TextField>
                    {(projectOptions.length > 0 || task?.projectId) && ( // P1-A project
                        <ProjectSelect options={projectOptions} value={projectId} label="Project (optional)" onChange={setProjectId}
                            currentLabel={task?.projectId ? projectTagLabel(task.projectCode, task.projectName) : null}
                            data-testid="task-project" data-help="tasks.form.field.project" />
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button data-help="tasks.form.cancel" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button data-help="tasks.form.save" onClick={handleSubmit} variant="contained" disabled={saving}>
                    {task ? 'Save' : 'Assign'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TaskForm;
